import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUp, Eye, Heart, Sprout, Feather, MessageCircle, Trash2 } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import PullToRefresh from "../components/PullToRefresh";
import CorrelationModal from "../components/CorrelationModal";
import CorrelationShareMenu from "../components/CorrelationShareMenu";
import MessageButton from "../components/MessageButton";
import UserLink from "../components/UserLink";

// Love/empathy-flavoured reaction taxonomy. Same shape as the old likes/hearts
// but each one names a felt response that the archive is in service of.
// On mobile we surface only "Resonates" (the heart) to keep the card calm —
// the other three reveal at sm: (≥640px). desktopOnly flag drives the visibility.
const REACTIONS = [
  { id: "resonates", label: "Resonates",     Icon: Heart,   color: "hsl(var(--destructive))", desktopOnly: false },
  { id: "growth",    label: "Grew me",       Icon: Sprout,  color: "hsl(140 50% 40%)",        desktopOnly: true  },
  { id: "peace",     label: "Brought peace", Icon: Feather, color: "hsl(210 50% 50%)",        desktopOnly: true  },
  { id: "seen",      label: "Felt seen",     Icon: Eye,     color: "hsl(var(--primary))",     desktopOnly: true  },
];

const TABS = [
  { id: "recent", label: "Recent", sub: "Newly shared" },
  { id: "hot",    label: "Hot",    sub: "Most bumped" },
];

function derivePullQuote(item) {
  // Prefer an explicit pull_quote field on the save (future-proof — not
  // written by the backend today, but reserved for a curator-picked line).
  if (item.pull_quote && String(item.pull_quote).trim()) {
    return String(item.pull_quote).trim();
  }
  // Bullets are the Oracle's own "distilled overlap" — the first one is
  // usually the thesis. Cap length so a bullet-of-a-paragraph doesn't dominate.
  if (Array.isArray(item.bullets) && item.bullets[0]) {
    const b = String(item.bullets[0]).trim();
    return b.length > 280 ? b.slice(0, 260).trimEnd() + "…" : b;
  }
  // Otherwise pull the first sentence out of the full response, skipping any
  // leading markdown heading (e.g. "**The Higher Self**").
  const text = String(item.response || item.body || "").trim();
  if (!text) return "";
  const lines = text.split(/\n+/).map((l) => l.trim()).filter(Boolean);
  const firstBody = lines.find((l) => !l.match(/^(#|\*+[^*]+\*+$)/)) || lines[0] || "";
  const match = firstBody.match(/^[^.!?]+[.!?]/);
  const line = match ? match[0].trim() : firstBody;
  return line.length > 280 ? line.slice(0, 260).trimEnd() + "…" : line;
}


function readInitialTab(hash) {
  const h = (hash || "").replace(/^#/, "").toLowerCase();
  // Item deep-links (#item-<id>) should NOT change the tab.
  if (h.startsWith("item-")) return null;
  // `readInitialTab` reads `#hot`, `#recent`, etc. from the URL fragment.
  // Fall through to "recent" as the default landing view — seekers open
  // Weekly to see what's *new*; Hot rewards them once they've scrolled.
  return TABS.find((t) => t.id === h)?.id || "recent";
}

// Honor a ?sort=recent query param (used by `new_correlation` notification
// deep-links so a brand-new 0-bump item isn't buried under the Hot sort).
function readInitialTabFromSearch() {
  if (typeof window === "undefined") return null;
  const s = new URLSearchParams(window.location.search).get("sort");
  return TABS.find((t) => t.id === (s || "").toLowerCase())?.id || null;
}

export default function Weekly() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(() =>
    readInitialTabFromSearch()
    || readInitialTab(typeof window !== "undefined" ? window.location.hash : "")
    || "recent"
  );
  const [viewed, setViewed] = useState(() => new Set()); // ids we've already pinged /view for this page
  const [query, setQuery] = useState("");

  const fetchFeed = useCallback(async (sort) => {
    setLoading(true);
    try {
      const path = user ? `/correlations/feed/me?sort=${sort}` : `/correlations/feed?sort=${sort}`;
      const { data } = await api.get(path);
      setItems(data || []);
    } catch (err) {
      console.error("weekly fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { fetchFeed(tab); }, [tab, fetchFeed]);

  // Deep-link: if the URL hash is `#item-<id>` (set by Saves' "View in Weekly"
  // or by the /api/share/correlation/<id> redirect), scroll to the article and
  // pulse a highlight. Also — rewrite the browser-bar URL to the canonical
  // shareable URL (/api/share/correlation/<id>) so that if the visitor
  // instinctively copies from their address bar to re-share, they get the URL
  // that social crawlers can actually unfurl (hash fragments are stripped by
  // every crawler, so the raw #item-… URL unfurls to the generic feed).
  useEffect(() => {
    if (loading) return;
    const hash = (window.location.hash || "").replace(/^#/, "");
    if (!hash.startsWith("item-")) return;
    const id = hash.slice(5);
    if (!id || !items.some((it) => it.id === id)) return;
    // Defer to next frame so layout has settled.
    const t = window.setTimeout(() => {
      const el = document.querySelector(`[data-testid="weekly-item-${CSS.escape(id)}"]`);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("weekly-deeplink-pulse");
      window.setTimeout(() => el.classList.remove("weekly-deeplink-pulse"), 2200);
      // Rewrite the URL in-place (no navigation, no history entry) to the
      // canonical crawler-friendly share URL. If the user reloads on the new
      // URL, the backend redirects them right back here — safe round-trip.
      try {
        const shareable = `${window.location.origin}/api/share/correlation/${id}`;
        window.history.replaceState(null, "", shareable);
      } catch (err) {
        // Non-fatal — some sandboxed contexts block replaceState.
        console.warn("[weekly] address-bar rewrite skipped:", err);
      }
    }, 50);
    return () => window.clearTimeout(t);
  }, [items, loading]);

  // Listen for hash changes (in-app deeplinks) so the tab updates without a full reload.
  useEffect(() => {
    const onHash = () => {
      const next = readInitialTab(window.location.hash);
      if (next) setTab(next);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  // Single source of truth — push to URL hash for shareable links.
  const switchTab = (id) => {
    setTab(id);
    if (window.location.hash !== `#${id}`) {
      window.history.replaceState(null, "", `/weekly#${id}`);
    }
  };

  // Fire-and-forget view tracking once per session per item.
  const trackView = useCallback(async (id) => {
    if (viewed.has(id)) return;
    setViewed((cur) => { const next = new Set(cur); next.add(id); return next; });
    try { await api.post(`/correlations/${id}/view`); } catch { /* ignore */ }
  }, [viewed]);

  // Delete a Weekly post you own. Confirmation → optimistic remove → API call
  // → rollback on failure. Uses the existing /oracle/saves/{id} endpoint which
  // enforces user_id === owner, so a hostile caller can't nuke someone else's
  // post even if they craft the request by hand.
  const onDeletePost = async (id) => {
    if (!user) return;
    if (!window.confirm("Delete this post? This can't be undone.")) return;
    const prev = items;
    setItems((cur) => cur.filter((it) => it.id !== id));
    try {
      await api.delete(`/oracle/saves/${id}`);
      toast.success("Post deleted.");
    } catch (err) {
      setItems(prev);
      toast.error(err?.response?.data?.detail || "Could not delete.");
    }
  };

  const onBumpToggle = async (id, currentBumped) => {    if (!user) { toast.error("Sign in to bump."); return; }
    // Optimistic update
    setItems((cur) => cur.map((it) => it.id === id
      ? { ...it, my_bump: !currentBumped, bumps_count: Math.max(0, (it.bumps_count || 0) + (currentBumped ? -1 : 1)) }
      : it,
    ));
    try {
      if (currentBumped) await api.delete(`/correlations/${id}/bump`);
      else await api.post(`/correlations/${id}/bump`);
    } catch {
      // Rollback on failure
      setItems((cur) => cur.map((it) => it.id === id
        ? { ...it, my_bump: currentBumped, bumps_count: Math.max(0, (it.bumps_count || 0) + (currentBumped ? 1 : -1)) }
        : it,
      ));
      toast.error("Could not bump.");
    }
  };

  const onReactToggle = async (id, kind) => {
    if (!user) { toast.error("Sign in to react."); return; }
    const item = items.find((it) => it.id === id);
    const has = (item?.my_reactions || []).includes(kind);
    setItems((cur) => cur.map((it) => {
      if (it.id !== id) return it;
      const my = new Set(it.my_reactions || []);
      if (has) my.delete(kind); else my.add(kind);
      return { ...it, my_reactions: [...my], reactions_count: Math.max(0, (it.reactions_count || 0) + (has ? -1 : 1)) };
    }));
    try {
      if (has) await api.delete(`/correlations/${id}/react/${kind}`);
      else await api.post(`/correlations/${id}/react`, { kind });
    } catch {
      // Rollback
      setItems((cur) => cur.map((it) => {
        if (it.id !== id) return it;
        const my = new Set(it.my_reactions || []);
        if (has) my.add(kind); else my.delete(kind);
        return { ...it, my_reactions: [...my], reactions_count: Math.max(0, (it.reactions_count || 0) + (has ? 1 : -1)) };
      }));
      toast.error("Could not react.");
    }
  };

  const tabSub = useMemo(() => TABS.find((t) => t.id === tab)?.sub || "", [tab]);

  // Client-side filter on the loaded feed.
  //   "@handle"     → filter by author handle
  //   plain text    → match against title, caption, bullets, response, and handle
  // Trimmed, lowercased, multi-word AND match.
  const visibleItems = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return items;
    if (q.startsWith("@")) {
      const needle = q.slice(1).replace(/^\s+/, "");
      if (!needle) return items;
      return items.filter((it) => (it.user_handle || "").toLowerCase().includes(needle));
    }
    const tokens = q.split(/\s+/).filter(Boolean);
    return items.filter((it) => {
      const hay = [
        it.title,
        it.caption,
        it.user_handle ? `@${it.user_handle}` : "",
        it.user_name,
        it.response,
        ...(it.bullets || []),
      ].filter(Boolean).join(" \n ").toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [items, query]);

  const [modalItem, setModalItem] = useState(null);

  return (
    <PullToRefresh onRefresh={async () => fetchFeed(tab)}>
    <div data-testid="weekly-page" className="max-w-5xl mx-auto px-6 sm:px-10 py-20">
      <div className="label mb-6 text-[hsl(var(--accent))]">Convergences</div>
      <h1 className="serif text-5xl sm:text-6xl text-foreground">
        Lately, these threads have surfaced.
      </h1>
      <p className="mt-6 text-muted-foreground max-w-2xl leading-relaxed">
        Correlations shared by readers. Bump the ones that land for you; the most-bumped rise to Hot. Bring your own through the Oracle.
      </p>

      {/* TAB STRIP */}
      <div role="tablist" aria-label="Sort" className="mt-12 flex items-center gap-8 hairline-b pb-4">
        {TABS.map((t) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={tab === t.id}
            data-testid={`weekly-tab-${t.id}`}
            onClick={() => switchTab(t.id)}
            className={`mono text-[11px] uppercase tracking-[0.22em] link-underline ${
              tab === t.id ? "text-[hsl(var(--primary))]" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            {t.label}
          </button>
        ))}
        <span className="ml-auto mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/70">{tabSub}</span>
      </div>

      {/* SEARCH / FILTER */}
      <div className="mt-6 flex items-center gap-3 flex-wrap">
        <input
          type="search"
          data-testid="weekly-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by topic, or @handle…"
          className="input-line flex-1 min-w-[200px] text-[15px] py-2"
          aria-label="Search the feed"
        />
        {query && (
          <button
            type="button"
            data-testid="weekly-search-clear"
            onClick={() => setQuery("")}
            className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground hover:text-foreground"
          >
            Clear search
          </button>
        )}
        {query && (
          <span
            data-testid="weekly-search-count"
            className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/80"
          >
            {visibleItems.length} of {items.length}
          </span>
        )}
      </div>

      <div className="mt-10 space-y-px bg-border">
        {loading && (
          <div className="bg-card p-16 text-center text-muted-foreground/75 italic serif text-xl">
            Gathering threads…
          </div>
        )}
        {!loading && items.length === 0 && (
          <div className="bg-card p-16 text-center" data-testid="weekly-empty">
            <p className="serif italic text-xl text-muted-foreground">
              Nothing has surfaced yet.
            </p>
            <Link to="/oracle" className="btn-gold mt-8 inline-block">Ask the Oracle</Link>
          </div>
        )}
        {!loading && items.length > 0 && visibleItems.length === 0 && (
          <div className="bg-card p-16 text-center" data-testid="weekly-search-empty">
            <p className="serif italic text-xl text-muted-foreground">
              No matches for "{query}".
            </p>
            <button
              type="button"
              onClick={() => setQuery("")}
              className="btn-ghost mt-6"
              data-testid="weekly-search-empty-clear"
            >
              Clear search
            </button>
          </div>
        )}
        {!loading && visibleItems.map((it, i) => (
          <WeeklyCard
            key={it.id}
            item={it}
            rank={i + 1}
            currentUserId={user?.user_id}
            onMount={() => trackView(it.id)}
            onBump={() => onBumpToggle(it.id, !!it.my_bump)}
            onReact={(kind) => onReactToggle(it.id, kind)}
            onExpand={() => setModalItem(it)}
            onDelete={() => onDeletePost(it.id)}
          />
        ))}
      </div>
      {modalItem && (
        <CorrelationModal
          item={modalItem}
          onClose={() => setModalItem(null)}
          onCommentCountChange={(id, delta) => {
            setItems((cur) => cur.map((it) =>
              it.id === id
                ? { ...it, comments_count: Math.max(0, (it.comments_count || 0) + delta) }
                : it
            ));
            setModalItem((cur) => (cur && cur.id === id
              ? { ...cur, comments_count: Math.max(0, (cur.comments_count || 0) + delta) }
              : cur));
          }}
        />
      )}
    </div>
    </PullToRefresh>
  );
}

function WeeklyCard({ item, rank, currentUserId, onMount, onBump, onReact, onExpand, onDelete }) {
  useEffect(() => { onMount?.(); /* once per mount */ }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const isOwner = !!currentUserId && item.user_id === currentUserId;

  const myRx = new Set(item.my_reactions || []);
  const summary = (item.response || "").trim();
  const hasMoreBody = summary.length > 0;

  // A "pull-quote" line — the most quotable fragment of the Oracle's answer.
  // Priority:
  //   1. Explicit `pull_quote` field (future-proof; not written today)
  //   2. First bullet (distilled overlap the Oracle already picked)
  //   3. First sentence of the full response
  const pullQuote = derivePullQuote(item);

  // The seeker's question — falls back to the auto-generated title when the
  // save has no `question` (migrated / legacy items).
  const seekerQuestion = (item.question || item.title || "").trim();

  return (
    <article
      id={`item-${item.id}`}
      data-testid={`weekly-item-${item.id}`}
      className="bg-card p-8 sm:p-10 flex gap-4 sm:gap-6"
    >
      {/* AUTHOR AVATAR — top-left. Clickable to profile. Falls back to an
          initial medallion when the author has no profile picture (e.g. email
          signups without an uploaded avatar). */}
      <div className="flex-shrink-0">
        <Link
          to={`/u/${item.user_handle || item.user_id}`}
          data-testid={`weekly-avatar-${item.id}`}
          aria-label={`Open ${item.user_name || "reader"}'s profile`}
          className="block"
        >
          {item.user_picture ? (
            <img
              src={item.user_picture}
              alt=""
              className="w-11 h-11 sm:w-12 sm:h-12 rounded-full object-cover ring-1 ring-border"
            />
          ) : (
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-base text-foreground/70">
              {((item.user_name || item.user_handle || "?")[0] || "?").toUpperCase()}
            </div>
          )}
        </Link>
      </div>

      {/* BODY */}
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-3 flex-wrap mb-2">
          <span className="serif text-xl text-muted-foreground/40 tabular-nums">{String(rank).padStart(2, "0")}</span>
          {item.source_type && <span className={`tag tag-${item.source_type}`}>{item.source_type}</span>}
          <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground/75 ml-auto inline-flex items-center gap-2 flex-wrap">
            by{" "}
            <UserLink
              handle={item.user_handle}
              userId={item.user_id}
              name={item.user_name}
              showAt
              className="text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
              testid={`weekly-author-${item.id}`}
            />
            {(item.user_handle || item.user_id) && (
              <MessageButton
                slug={item.user_handle || item.user_id}
                ownerUserId={item.user_id}
                testid={`weekly-msg-${item.id}`}
              />
            )}
            {isOwner && (
              <button
                type="button"
                data-testid={`weekly-delete-${item.id}`}
                onClick={onDelete}
                title="Delete this post"
                className="inline-flex items-center gap-1 text-muted-foreground/60 hover:text-[hsl(var(--destructive))] transition-colors"
              >
                <Trash2 size={12} strokeWidth={1.75} />
                <span className="sr-only">Delete</span>
              </button>
            )}
          </span>
        </div>

        {/* CAPTION — the seeker's own voice sits at the top of the post,
            functioning as the lede. Auto-generated titles from the Oracle
            question have been retired here — the caption is the header. */}
        {item.caption ? (
          <blockquote
            data-testid={`caption-${item.id}`}
            className="pl-4 border-l-2 border-[hsl(var(--primary))] text-foreground serif italic text-lg leading-relaxed whitespace-pre-wrap"
          >
            {item.caption}
          </blockquote>
        ) : (
          // Legacy / uncaptioned posts still need a heading — fall back to the
          // stored title so nothing is orphaned.
          item.title && (
            <h2 className="serif text-2xl sm:text-3xl text-foreground leading-tight">{item.title}</h2>
          )
        )}

        {/* SEEKER → ORACLE — the actual Q&A. Question shown as a soft italic
            line under a small label; the Oracle's answer surfaces as a
            pull-quote (one striking line) with the full response one tap away. */}
        {(seekerQuestion || pullQuote) && (
          <div className="mt-8 space-y-6">
            {seekerQuestion && (
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/85 mb-2">
                  The Seeker asked
                </div>
                <p
                  data-testid={`seeker-question-${item.id}`}
                  className="serif italic text-lg sm:text-xl text-foreground/85 leading-snug"
                >
                  {seekerQuestion}
                </p>
              </div>
            )}
            {pullQuote && (
              <div>
                <div className="mono text-[10px] uppercase tracking-[0.22em] text-muted-foreground/85 mb-2">
                  The Oracle
                </div>
                <blockquote
                  data-testid={`oracle-pullquote-${item.id}`}
                  className="pl-5 border-l-[3px] border-[hsl(var(--primary))] serif italic text-xl sm:text-2xl text-foreground leading-snug"
                >
                  {pullQuote}
                </blockquote>
              </div>
            )}
          </div>
        )}

        {/* READ FULL TOGGLE — opens floating modal */}
        {hasMoreBody && (
          <div className="mt-6">
            <button
              type="button"
              data-testid={`expand-${item.id}`}
              onClick={onExpand}
              className="mono text-[10px] uppercase tracking-[0.2em] text-[hsl(var(--accent))] link-underline"
            >
              Read the full breakdown →
            </button>
          </div>
        )}

        {/* REACTION STRIP — bump-arrow (left of heart), heart on mobile,
            all four reaction icons on sm+, then reactions count, comments,
            share, views. */}
        <div className="mt-5 flex items-center gap-1 flex-wrap">
          {/* Bump — moved here from the old left-hand column. Sits immediately
              left of the reaction icons ("just left of the like heart"). */}
          <button
            type="button"
            data-testid={`bump-${item.id}`}
            aria-pressed={!!item.my_bump}
            aria-label={item.my_bump ? "Remove bump" : "Bump this correlation"}
            onClick={onBump}
            title="Bump — signal-boost this post"
            className={`inline-flex items-center gap-1.5 mono text-[11px] tabular-nums px-2.5 py-1.5 border transition-colors mr-1 ${
              item.my_bump
                ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.08)] text-[hsl(var(--primary))]"
                : "border-transparent text-muted-foreground hover:border-border hover:text-[hsl(var(--primary))]"
            }`}
          >
            <ArrowUp size={13} strokeWidth={2.25} />
            <span data-testid={`bump-count-${item.id}`}>{item.bumps_count || 0}</span>
          </button>
          {REACTIONS.map((rx) => {
            const on = myRx.has(rx.id);
            return (
              <button
                key={rx.id}
                type="button"
                data-testid={`react-${rx.id}-${item.id}`}
                onClick={() => onReact(rx.id)}
                aria-pressed={on}
                title={rx.label}
                className={`${rx.desktopOnly ? "hidden sm:inline-flex" : "inline-flex"} items-center gap-1.5 mono text-[11px] tabular-nums px-2.5 py-1.5 border transition-colors ${
                  on
                    ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.08)]"
                    : "border-transparent text-muted-foreground hover:border-border hover:text-foreground"
                }`}
                style={on ? { color: rx.color } : undefined}
              >
                <rx.Icon size={13} strokeWidth={2} fill={on && rx.id === "resonates" ? "currentColor" : "none"} />
                <span className="sr-only sm:not-sr-only">{rx.label}</span>
              </button>
            );
          })}
          <span
            data-testid={`reactions-count-${item.id}`}
            className="mono text-[11px] tabular-nums text-muted-foreground ml-2"
          >
            {item.reactions_count || 0}
          </span>
          <button
            type="button"
            data-testid={`comments-count-${item.id}`}
            onClick={onExpand}
            title="Read comments"
            className="inline-flex items-center gap-1.5 mono text-[11px] tabular-nums text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors ml-3"
          >
            <MessageCircle size={13} strokeWidth={2} />
            <span>{item.comments_count || 0}</span>
          </button>
          <div className="ml-3" data-testid={`share-wrap-${item.id}`}>
            <CorrelationShareMenu correlation={item} testIdPrefix={`share-${item.id}`} />
          </div>
          <span
            data-testid={`views-${item.id}`}
            className="inline-flex items-center gap-1.5 mono text-[11px] tabular-nums text-muted-foreground ml-auto"
            title="Unique readers"
          >
            <Eye size={13} strokeWidth={2} />
            <span>{item.views_count || 0}</span>
          </span>
        </div>
      </div>
    </article>
  );
}
